/**
 * CompanyLiabilitiesPage — iter-237.6 (Feb 2026)
 *
 * Liabilities ledger for CloudGM FZ LLC.
 * `?mode=entry|records` splits the page into two focused views (or shows
 * both when omitted).
 *
 * Entry mode shows TWO forms per user's spec:
 *   1) Add a new liability      (creditor, amount, description, due date, …)
 *   2) Pay off an existing one  (pick unpaid liability → enter amount)
 *
 * Records mode shows:
 *   • Liabilities table with status badges (paid / partial / unpaid)
 *   • Payments log (all payments across all liabilities)
 *
 * Backend routes (see routes/admin_company_liabilities.py):
 *   GET|POST|PATCH|DELETE /api/admin/company-liabilities[/{id}]
 *   GET|POST              /api/admin/company-liabilities/{id}/payments
 *   DELETE                /api/admin/company-liabilities/payments/{payment_id}
 *   GET                    /api/admin/company-liabilities/records/payments
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  HandCoins, Plus, Trash2, Loader2, RefreshCcw, CheckCircle2, CircleDollarSign,
  AlertCircle, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { DirhamPrice } from '../../components/DirhamPrice';
import { floorAED } from '../../utils/money';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/company-liabilities`;

const EMPTY_LIABILITY = {
  creditor_name: '', amount_aed: '', description: '',
  due_date: '', category: '', notes: '',
};
const EMPTY_PAYMENT = {
  liability_id: '', amount_aed: '', payment_date: '',
  payment_method: '', reference: '', notes: '',
};

export default function CompanyLiabilitiesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get('mode') || '').toLowerCase();
  const isEntryMode   = mode === 'entry';
  const isRecordsMode = mode === 'records';
  const parentPath = isEntryMode
    ? '/finance/asb606/payments/entry'
    : isRecordsMode
      ? '/finance/asb606/payments/records'
      : '/finance/asb606/payments';
  const parentLabelAr = isEntryMode ? 'الإدراج المالي للشركة'
                     : isRecordsMode ? 'السجل والمتابعة'
                     : 'المدفوعات المالية للشركة';
  const parentLabelEn = isEntryMode ? 'Financial Entry'
                     : isRecordsMode ? 'Records & Tracking'
                     : 'Company Financials';

  const [liabilities, setLiabilities] = useState([]);
  const [totals, setTotals] = useState({ amount_aed: 0, paid_aed: 0, remaining_aed: 0, counts: {} });
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingL, setSavingL] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const [liabForm, setLiabForm] = useState(EMPTY_LIABILITY);
  const [payForm,  setPayForm]  = useState(EMPTY_PAYMENT);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rLiabs, rPayments] = await Promise.all([
        axios.get(`${API}/`, { headers }),
        axios.get(`${API}/records/payments`, { headers }),
      ]);
      setLiabilities(rLiabs.data?.data || []);
      setTotals(rLiabs.data?.totals || { amount_aed: 0, paid_aed: 0, remaining_aed: 0, counts: {} });
      setPayments(rPayments.data?.data || []);
    } catch (e) {
      toast.error(t('فشل تحميل المستحقات', 'Failed to load liabilities'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ────────────── Add new liability ──────────────
  const saveLiability = async () => {
    if (!liabForm.creditor_name.trim()) {
      toast.error(t('اسم الدائن مطلوب', 'Creditor name is required'));
      return;
    }
    if (!liabForm.amount_aed || Number(liabForm.amount_aed) <= 0) {
      toast.error(t('المبلغ مطلوب وأكبر من صفر', 'Amount required and > 0'));
      return;
    }
    setSavingL(true);
    try {
      const payload = { ...liabForm, amount_aed: Number(liabForm.amount_aed) };
      await axios.post(`${API}/`, payload, { headers });
      toast.success(t('تمت إضافة المستحق', 'Liability added'));
      setLiabForm({ ...EMPTY_LIABILITY });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSavingL(false);
    }
  };

  // ────────────── Pay against a liability ──────────────
  const savePayment = async () => {
    if (!payForm.liability_id) {
      toast.error(t('اختر المستحق المراد تسديده', 'Pick a liability to pay against'));
      return;
    }
    if (!payForm.amount_aed || Number(payForm.amount_aed) <= 0) {
      toast.error(t('مبلغ التسديد مطلوب', 'Payment amount required'));
      return;
    }
    setSavingP(true);
    try {
      const payload = {
        amount_aed: Number(payForm.amount_aed),
        payment_date: payForm.payment_date || undefined,
        payment_method: payForm.payment_method || undefined,
        reference: payForm.reference || undefined,
        notes: payForm.notes || undefined,
      };
      await axios.post(`${API}/${payForm.liability_id}/payments`, payload, { headers });
      toast.success(t('تم تسجيل التسديد', 'Payment recorded'));
      setPayForm({ ...EMPTY_PAYMENT });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل التسديد', 'Payment failed'));
    } finally {
      setSavingP(false);
    }
  };

  const deleteLiability = async (id) => {
    if (!window.confirm(t('حذف المستحق وكل تسديداته؟', 'Delete this liability AND its payments?'))) return;
    try {
      await axios.delete(`${API}/${id}`, { headers });
      toast.success(t('تم الحذف', 'Deleted'));
      fetchAll();
    } catch (e) {
      toast.error(t('فشل الحذف', 'Delete failed'));
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm(t('حذف هذا التسديد؟', 'Delete this payment?'))) return;
    try {
      await axios.delete(`${API}/payments/${id}`, { headers });
      toast.success(t('تم الحذف', 'Deleted'));
      fetchAll();
    } catch (e) {
      toast.error(t('فشل الحذف', 'Delete failed'));
    }
  };

  const unpaidLiabilities = liabilities.filter((l) => l.status !== 'paid');

  return (
    <DashboardLayout
      title="مستحقات الشركة"
      titleEn="Company Liabilities"
      subtitle={isEntryMode ? 'إضافة مستحق أو تسديد مستحق' : 'المستحقات والتسديدات'}
      subtitleEn={isEntryMode ? 'Add a liability or record a payment' : 'Liabilities & payments log'}
      screenCode="ASB-FIN-PAY-LIAB"
      backPath={parentPath}
      onBack={() => navigate(parentPath)}
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'ASB606 (المالية)', labelEn: 'ASB606 (Finance)', path: '/finance/asb606' },
        { labelAr: parentLabelAr, labelEn: parentLabelEn, path: parentPath },
        { labelAr: 'مستحقات الشركة', labelEn: 'Liabilities' },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="company-liabilities-page">

        {/* Totals (records + default) */}
        {!isEntryMode && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatTile color="#EF4444" icon={AlertCircle}   labelAr="إجمالي المستحقات"  labelEn="Total Liabilities" value={totals.amount_aed}    count={liabilities.length} testId="liab-total" />
            <StatTile color="#10B981" icon={CheckCircle2}  labelAr="المدفوع"           labelEn="Paid"              value={totals.paid_aed}      count={totals.counts?.paid || 0} testId="liab-paid" />
            <StatTile color="#F59E0B" icon={Clock}          labelAr="المتبقي"           labelEn="Remaining"         value={totals.remaining_aed} count={totals.counts?.partial || 0} testId="liab-remaining" />
            <StatTile color="#3B82F6" icon={CircleDollarSign} labelAr="غير مدفوع"     labelEn="Unpaid"            value={0}                    count={totals.counts?.unpaid || 0} testId="liab-unpaid" onlyCount />
          </div>
        )}

        {/* Entry forms */}
        {!isRecordsMode && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {/* Add liability */}
            <div className="bg-white/[0.02] border border-red-500/30 rounded-2xl p-5" data-testid="liab-form-add">
              <div className="flex items-center gap-2 mb-4">
                <Plus className="h-5 w-5 text-red-400" />
                <h3 className="font-semibold text-white">{t('إضافة مستحق على الشركة', 'Add liability')}</h3>
              </div>
              <div className="space-y-3">
                <LabeledInput label={t('لمن (اسم/جهة) *', 'Creditor *')} value={liabForm.creditor_name} onChange={(v) => setLiabForm((f) => ({ ...f, creditor_name: v }))} testid="liab-creditor" />
                <LabeledInput label={t('المبلغ (د.إ) *', 'Amount (AED) *')} type="number" value={liabForm.amount_aed} onChange={(v) => setLiabForm((f) => ({ ...f, amount_aed: v }))} testid="liab-amount" />
                <LabeledInput label={t('الوصف', 'Description')} value={liabForm.description} onChange={(v) => setLiabForm((f) => ({ ...f, description: v }))} testid="liab-desc" />
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label={t('تاريخ الاستحقاق', 'Due Date')} type="date" value={liabForm.due_date} onChange={(v) => setLiabForm((f) => ({ ...f, due_date: v }))} testid="liab-due-date" />
                  <LabeledInput label={t('التصنيف', 'Category')} value={liabForm.category} onChange={(v) => setLiabForm((f) => ({ ...f, category: v }))} placeholder={t('مورد / بنك / ...', 'Supplier / Bank / ...')} testid="liab-category" />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">{t('ملاحظات', 'Notes')}</label>
                  <Textarea value={liabForm.notes} onChange={(e) => setLiabForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="bg-[#0a0a0a] border-white/10 text-white" data-testid="liab-notes" />
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveLiability} disabled={savingL} className="bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-xl" data-testid="liab-save-add">
                    {savingL ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 me-2" />{t('إضافة مستحق', 'Add liability')}</>)}
                  </Button>
                </div>
              </div>
            </div>

            {/* Pay a liability */}
            <div className="bg-white/[0.02] border border-emerald-500/30 rounded-2xl p-5" data-testid="liab-form-pay">
              <div className="flex items-center gap-2 mb-4">
                <CircleDollarSign className="h-5 w-5 text-emerald-400" />
                <h3 className="font-semibold text-white">{t('تسديد مستحق موجود', 'Pay an existing liability')}</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">{t('اختر المستحق *', 'Pick a liability *')}</label>
                  <select
                    value={payForm.liability_id}
                    onChange={(e) => setPayForm((f) => ({ ...f, liability_id: e.target.value }))}
                    className="w-full h-10 bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 text-sm"
                    data-testid="liab-pay-picker"
                  >
                    <option value="">{t('— اختر —', '— Select —')}</option>
                    {unpaidLiabilities.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.creditor_name} — {t('متبقي', 'Remaining')}: {floorAED(l.remaining_aed || l.amount_aed).toFixed(2)} د.إ
                      </option>
                    ))}
                  </select>
                  {unpaidLiabilities.length === 0 && (
                    <p className="text-[10px] text-white/40 mt-1">{t('لا توجد مستحقات غير مسدّدة', 'No unpaid liabilities')}</p>
                  )}
                </div>
                <LabeledInput label={t('مبلغ التسديد (د.إ) *', 'Payment amount (AED) *')} type="number" value={payForm.amount_aed} onChange={(v) => setPayForm((f) => ({ ...f, amount_aed: v }))} testid="liab-pay-amount" />
                <div className="grid grid-cols-2 gap-3">
                  <LabeledInput label={t('تاريخ التسديد', 'Payment Date')} type="date" value={payForm.payment_date} onChange={(v) => setPayForm((f) => ({ ...f, payment_date: v }))} testid="liab-pay-date" />
                  <LabeledInput label={t('طريقة الدفع', 'Payment Method')} value={payForm.payment_method} onChange={(v) => setPayForm((f) => ({ ...f, payment_method: v }))} placeholder={t('نقدي / حوالة / ...', 'Cash / Transfer / ...')} testid="liab-pay-method" />
                </div>
                <LabeledInput label={t('المرجع', 'Reference')} value={payForm.reference} onChange={(v) => setPayForm((f) => ({ ...f, reference: v }))} testid="liab-pay-ref" />
                <div className="flex justify-end">
                  <Button onClick={savePayment} disabled={savingP} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6 rounded-xl" data-testid="liab-save-pay">
                    {savingP ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 me-2" />{t('تسجيل التسديد', 'Record payment')}</>)}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Records — liabilities table */}
        {!isEntryMode && (
          <>
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-4" data-testid="liab-list">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white inline-flex items-center gap-2">
                  <HandCoins className="h-5 w-5 text-red-400" />
                  {t('سجل المستحقات', 'Liabilities Ledger')}
                  <span className="text-xs font-normal text-white/50 ms-2">({liabilities.length})</span>
                </h3>
                <Button variant="outline" size="sm" onClick={fetchAll} className="border-white/10 text-white/70">
                  <RefreshCcw className="h-4 w-4" />
                </Button>
              </div>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white/40" />
                </div>
              ) : liabilities.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <HandCoins className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{t('لا توجد مستحقات مسجّلة', 'No liabilities yet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {liabilities.map((l) => (
                    <LiabilityRow key={l.id} row={l} onDelete={deleteLiability} t={t} />
                  ))}
                </div>
              )}
            </div>

            {/* Payments log */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid="liab-payments-list">
              <h3 className="font-semibold text-white inline-flex items-center gap-2 mb-4">
                <CircleDollarSign className="h-5 w-5 text-emerald-400" />
                {t('سجل تسديدات المستحقات', 'Liability Payments Log')}
                <span className="text-xs font-normal text-white/50 ms-2">({payments.length})</span>
              </h3>
              {payments.length === 0 ? (
                <div className="text-center py-10 text-white/40">
                  <p className="text-sm">{t('لا توجد تسديدات بعد', 'No payments yet')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {payments.map((p) => (
                    <PaymentRow key={p.id} row={p} onDelete={deletePayment} t={t} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </DashboardLayout>
  );
}

function LabeledInput({ label, value, onChange, type = 'text', placeholder, dir, testid }) {
  return (
    <div>
      <label className="block text-xs text-white/60 mb-1">{label}</label>
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className="bg-[#0a0a0a] border-white/10 text-white h-10"
        data-testid={testid}
      />
    </div>
  );
}

function StatTile({ color, icon: Icon, labelAr, labelEn, value, count, testId, onlyCount }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  return (
    <div
      className="rounded-xl border p-4"
      style={{ backgroundColor: `${color}0f`, borderColor: `${color}40` }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs" style={{ color }}>{isAr ? labelAr : labelEn}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      {!onlyCount && (
        <p className="text-white font-bold text-lg">
          <DirhamPrice value={floorAED(value || 0)} decimals={2} />
        </p>
      )}
      <p className="text-[10px] text-white/40 mt-0.5">{count || 0}</p>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const cfg = {
    paid:    { color: '#10B981', label: t('مسدّد',     'Paid'),    Icon: CheckCircle2 },
    partial: { color: '#F59E0B', label: t('جزئي',      'Partial'), Icon: Clock },
    unpaid:  { color: '#EF4444', label: t('غير مسدّد', 'Unpaid'),  Icon: AlertCircle },
  }[status] || { color: '#64748B', label: status, Icon: AlertCircle };
  const Icon = cfg.Icon;
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
      style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function LiabilityRow({ row, onDelete, t }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0a0a0a] border border-white/5 p-3 hover:border-white/20 transition-colors" data-testid={`liab-row-${row.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-white font-semibold truncate">{row.creditor_name}</p>
          <StatusBadge status={row.status} t={t} />
        </div>
        {row.description && <p className="text-xs text-white/50 mt-0.5 truncate">{row.description}</p>}
        {row.due_date && <p className="text-[10px] text-white/40 mt-0.5">{t('استحقاق', 'Due')}: {row.due_date}</p>}
      </div>
      <div className="text-end shrink-0 min-w-[110px]">
        <p className="text-white font-bold"><DirhamPrice value={floorAED(row.amount_aed || 0)} decimals={2} /></p>
        <p className="text-[10px] text-emerald-400">{t('مدفوع', 'Paid')}: <DirhamPrice value={floorAED(row.paid_aed || 0)} decimals={2} /></p>
        <p className="text-[10px] text-amber-400">{t('متبقي', 'Remaining')}: <DirhamPrice value={floorAED(row.remaining_aed || 0)} decimals={2} /></p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDelete(row.id)}
        className="h-8 px-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
        data-testid={`liab-delete-${row.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function PaymentRow({ row, onDelete, t }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0a0a0a] border border-white/5 p-3 hover:border-white/20 transition-colors" data-testid={`liab-payment-row-${row.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm">
          <span className="text-emerald-400 font-semibold">
            <DirhamPrice value={floorAED(row.amount_aed || 0)} decimals={2} />
          </span>
          <span className="text-white/40 mx-2">←</span>
          <span className="truncate">{row.liability_creditor || t('غير معروف', 'Unknown')}</span>
        </p>
        <p className="text-[10px] text-white/40 mt-0.5">
          {row.payment_date && (row.payment_date.split('T')[0])} · {row.payment_method || '—'} {row.reference && `· ${row.reference}`}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDelete(row.id)}
        className="h-8 px-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
        data-testid={`liab-payment-delete-${row.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
